/**
 * Hero section: intro copy (HTML) and "Start Assessment" CTA.
 * Scrolls to first section when the user clicks Start.
 */
import React, { useMemo, memo } from 'react';

import { Button } from './ui/button';
import { sanitizeHtml, isHtmlSafe } from '../utils/sanitize';
import { cn } from '@/lib/utils';

interface HeroProps {
  content: string;
  onStartAssessment: () => void;
}

const HeroComponent: React.FC<HeroProps> = ({ content, onStartAssessment }) => {
  // Sanitize HTML content for XSS protection (defense in depth)
  const sanitizedContent = useMemo(() => {
    if (!content || typeof content !== 'string') {
      return '';
    }
    const trimmed = content.trim();
    if (!trimmed) {
      return '';
    }
    // Quick check first, then full sanitization if needed
    if (isHtmlSafe(trimmed)) {
      return trimmed;
    }
    if (process.env.NODE_ENV === 'development') {
      console.warn('Potentially unsafe HTML detected in Hero content, sanitizing...');
    }
    return sanitizeHtml(trimmed);
  }, [content]);

  const hasContent = sanitizedContent.length > 0;

  return (
    <section className="pt-24 pb-16 px-6" aria-label="Hero section">
      <div className="max-w-content mx-auto text-center">
        {hasContent && (
          <div
            className="prose prose-slate prose-lg max-w-none mx-auto [&>:first-child]:mt-0 [&>h1]:text-3xl [&>h1]:sm:text-4xl [&>h1]:font-bold [&>h1]:text-text-primary [&>h1]:leading-tight [&>h1]:mb-4 [&>p]:text-base [&>p]:text-text-secondary [&>p]:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        )}
        <Button
          onClick={onStartAssessment}
          size="default"
          className={cn('shadow-sm', hasContent ? 'mt-2' : 'mt-0')}
          aria-label="Start the AI-readiness assessment"
        >
          Start Assessment
        </Button>
      </div>
    </section>
  );
};

// Memoize to prevent unnecessary re-renders
export const Hero = memo(HeroComponent);
